import { layoutRects, STAGE_W, STAGE_H } from "@/components/studio/ProgramCanvas";
import { Tag, MessageCircleQuestion, Layers, Volume2 } from "lucide-react";

const pct = (v, total) => `${(v / total) * 100}%`;

/**
 * Light-mode stand-in for the Program preview: same layout geometry as ProgramCanvas but no
 * video decoding — just labelled boxes for what is on air plus the active overlays. The
 * recorder / room monitors still render the real picture.
 */
export default function ProgramSketch({ program, roster, registry, showTitle, className = "" }) {
  const layout = program?.layout || "solo";
  const ids = program?.sources || [];
  const byCustom = roster?.byCustomId || new Map();
  const speaker = layout === "speaker";
  const sources = speaker ? [{ label: "Auto speaker", sub: "follows whoever is talking", live: true }]
    : ids.map((id) => {
      const reg = registry?.get(id);
      const peer = byCustom.get(id);
      return { label: reg?.label || reg?.name || peer?.name || "Source", sub: reg?.kind === "camera" ? (reg?.room_cam ? "room cam" : "camera") : (reg?.kind || "guest"), live: !!peer?.videoEnabled, id };
    });
  const rects = layoutRects(layout, sources.length);
  const lt = program?.lower_third || {};
  const banner = program?.banner || {};
  const q = program?.question || {};

  return (
    <div className={`relative overflow-hidden bg-[#0b1017] ${className}`} data-testid="program-sketch">
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
      {sources.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-zinc-500">Holding card</p>
            {showTitle && <p className="mt-2 text-sm text-zinc-300">{showTitle}</p>}
          </div>
        </div>
      )}
      {rects.map((r, i) => {
        const s = sources[i];
        return (
          <div key={s.id || i} className={`absolute flex items-center justify-center border ${i === 0 ? "border-[#c9772e]/70 bg-[#c9772e]/10" : "border-white/25 bg-white/5"}`}
            style={{ left: pct(r.x, STAGE_W), top: pct(r.y, STAGE_H), width: pct(r.w, STAGE_W), height: pct(r.h, STAGE_H), borderRadius: r.radius ? 8 : 0 }} data-testid={`sketch-source-${s.id || i}`}>
            <div className="max-w-[90%] text-center">
              <p className="truncate text-sm font-semibold text-zinc-100">{speaker && <Volume2 className="mr-1 inline h-3.5 w-3.5 text-[#e6a25f]" />}{s.label}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">{s.sub}{!speaker && !s.live ? " · no video" : ""}</p>
            </div>
          </div>
        );
      })}
      <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-1.5 p-2" data-testid="sketch-overlays">
        <span className="rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300">{layout}</span>
        {lt.visible && (lt.name || lt.title) && <span className="inline-flex items-center gap-1 rounded bg-[#c9772e]/25 px-1.5 py-0.5 text-[10px] text-[#e6a25f]" data-testid="sketch-plate"><Tag className="h-3 w-3" /> {lt.name}{lt.title ? ` · ${lt.title}` : ""}</span>}
        {banner.visible && banner.text && <span className="inline-flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-zinc-200" data-testid="sketch-banner"><Layers className="h-3 w-3" /> {banner.text}</span>}
        {q.visible && q.text && <span className="inline-flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-zinc-200" data-testid="sketch-question"><MessageCircleQuestion className="h-3 w-3" /> Question on screen</span>}
      </div>
      <div className="absolute right-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Light mode · no video preview</div>
    </div>
  );
}
